"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useTheme } from "next-themes";

import { Footer, Nav } from "@/app/_components/sections";
import { IconArrowRight, IconArrowUpRight } from "@/app/_components/icons";
import { InstallSnippet } from "@/components/photon/install-snippet";
import {
  HARDWARE_CHIPS,
  familySlugForName,
  formatMatrixDate,
  rowsForChip,
  slugify,
} from "./photon-data";
import { PhotonPageMark } from "./photon-wordmark";

const DOCS_URL = "https://docs.moondream.ai/running-locally";
const RESULTS_URL = "/photon/performance";

const useLandingTheme = () => {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const theme: "dark" | "light" =
    mounted && resolvedTheme === "dark" ? "dark" : "light";
  return { theme, toggle: () => setTheme(theme === "dark" ? "light" : "dark") };
};

const ResultsLink = () => (
  <Link href={RESULTS_URL} className="family-cta ev-link">
    Results <IconArrowUpRight size={11} />
  </Link>
);

export function HardwarePage({ chip }: { chip: string }) {
  const { theme, toggle } = useLandingTheme();
  const rows = rowsForChip(chip);
  const first = rows[0];
  const lastVerified = rows
    .map((r) => r.lastVerified)
    .filter((d): d is string => !!d)
    .sort()
    .at(-1);

  return (
    <div className="md-landing">
      <Nav theme={theme} onToggleTheme={toggle} />

      <main>
        <section
          className="section"
          style={{ paddingTop: 72, paddingBottom: 48 }}
        >
          <div className="container">
            <PhotonPageMark section="Hardware" />
            <h1
              className="t-display-md"
              style={{ marginTop: 12, maxWidth: 780 }}
            >
              {chip}
            </h1>
            <p
              className="t-body-lg"
              style={{
                color: "var(--text-secondary)",
                marginTop: 16,
                maxWidth: 680,
              }}
            >
              {first.target} · validated on {first.testedHardware} ·{" "}
              {rows.length} {rows.length === 1 ? "model" : "models"} in
              production
              {lastVerified
                ? ` · last verified ${formatMatrixDate(lastVerified)}`
                : ""}
              .
            </p>
            <div className="lens-cta" style={{ marginTop: 18 }}>
              <Link href="/photon/hardware" className="lens-cta-link">
                All architectures
              </Link>
              <Link href="/photon/support" className="lens-cta-link">
                Support explorer <IconArrowRight size={12} />
              </Link>
            </div>
          </div>
        </section>

        <section className="section" style={{ paddingTop: 0, paddingBottom: 64 }}>
          <div className="container">
            <div className="matrix-panel">
              <table className="matrix-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Precision</th>
                    <th>Capabilities</th>
                    <th>Since</th>
                    <th>Last verified</th>
                    <th>Benchmark</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} id={row.id}>
                      <td>
                        <Link
                          href={`/photon/models/${familySlugForName(row.family)}`}
                          className="matrix-model"
                        >
                          {row.model}
                        </Link>
                        <span className="matrix-family mono">{row.family}</span>
                      </td>
                      <td className="mono tabular matrix-dim">
                        {row.precision.join(" · ")}
                      </td>
                      <td className="matrix-caps">
                        {row.capabilities.join(" · ")}
                      </td>
                      <td className="mono tabular matrix-dim">
                        {row.since ? `v${row.since}` : "–"}
                      </td>
                      <td className="mono tabular matrix-dim">
                        {row.lastVerified
                          ? formatMatrixDate(row.lastVerified)
                          : "–"}
                      </td>
                      <td>
                        {row.benchmarked ? (
                          <ResultsLink />
                        ) : (
                          <span className="matrix-dim">–</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="install-strip" style={{ marginTop: 32 }}>
              <div>
                <div className="t-heading-sm">Run on {chip}</div>
                <p
                  className="t-body-sm"
                  style={{ color: "var(--text-secondary)", marginTop: 4 }}
                >
                  Install the SDK and run any listed model locally.
                </p>
              </div>
              <InstallSnippet />
            </div>

            <p className="matrix-foot mono">
              <a href={DOCS_URL} target="_blank" rel="noreferrer">
                Quickstart docs
              </a>{" "}
              · <Link href="/photon/hardware">all architectures</Link> ·{" "}
              <Link href="/photon/updates">product updates</Link>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export function HardwareIndex() {
  const { theme, toggle } = useLandingTheme();

  return (
    <div className="md-landing">
      <Nav theme={theme} onToggleTheme={toggle} />

      <main>
        <section
          className="section"
          style={{ paddingTop: 72, paddingBottom: 48 }}
        >
          <div className="container">
            <PhotonPageMark section="Hardware" />
            <h1
              className="t-display-md"
              style={{ marginTop: 12, maxWidth: 780 }}
            >
              Architectures Photon runs on.
            </h1>
            <p
              className="t-body-lg"
              style={{
                color: "var(--text-secondary)",
                marginTop: 16,
                maxWidth: 680,
              }}
            >
              Every chip and platform with production support. Each page lists
              the models validated on that hardware.
            </p>
          </div>
        </section>

        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container">
            <div className="model-card-grid">
              {HARDWARE_CHIPS.map((chip) => {
                const rows = rowsForChip(chip);
                const first = rows[0];
                return (
                  <Link
                    key={chip}
                    href={`/photon/hardware/${slugify(chip)}`}
                    className="model-card"
                  >
                    <div className="model-card-head">
                      <h3>{chip}</h3>
                      <span>
                        {rows.length} {rows.length === 1 ? "model" : "models"}
                      </span>
                    </div>
                    <p>
                      {first.target} · {first.testedHardware}
                    </p>
                    <div className="model-card-cta">
                      Supported models <IconArrowRight size={13} />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
